import type { RequestInfo } from '@warp-drive/core/types/request';
import { SkipCache } from '@warp-drive/core-types/request';
import { buildBaseURL } from '@warp-drive/utilities';
import { CONTRACT_HEADER } from '../handlers/type-normalization-handler.ts';
import { SkipErrorReporting } from '../request-options.ts';

const RESOURCE_PATH = 'api/v2/user-preference-settings';

/** One of the signed-in person's own preference rows. */
export interface UserPreferenceSetting {
  subject: string;
  predicate: string;
  /** Free-form JSON; the API dasherizes nested keys, so single-word keys round-trip unchanged. */
  value: Record<string, unknown>;
}

function preferenceRequest(url: string, method: 'GET' | 'PUT', body?: unknown): RequestInfo {
  return {
    url,
    method,
    headers: new Headers({
      Accept: 'application/vnd.api+json',
      [CONTRACT_HEADER]: '2',
      ...(body === undefined ? {} : { 'Content-Type': 'application/vnd.api+json' }),
    }),
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    cacheOptions: { [SkipCache]: true },
    options: { [SkipErrorReporting]: true },
  };
}

/**
 * Reads the signed-in person's own preference rows for one subject, optionally narrowed to
 * one predicate, bypassing the client cache. The owner comes from the session server-side.
 * Failures skip the global toast and logout, so the caller decides what a failed read means.
 */
export function queryUserPreferenceSettingsRequest(filter: {
  subject: string;
  predicate?: string;
}) {
  const search = new URLSearchParams({ 'filter[subject]': filter.subject });
  if (filter.predicate) search.set('filter[predicate]', filter.predicate);
  return preferenceRequest(
    `${buildBaseURL({ resourcePath: RESOURCE_PATH })}?${search.toString()}`,
    'GET'
  );
}

/**
 * Creates or replaces the signed-in person's row for `subject` + `predicate`. Idempotent, so
 * the caller never tracks a row id; the API answers 422 for a blank subject or predicate.
 * Failures skip the global toast and logout, so the caller must report them.
 */
export function upsertUserPreferenceSettingRequest(setting: UserPreferenceSetting) {
  return preferenceRequest(
    buildBaseURL({ resourcePath: `${RESOURCE_PATH}/upsert` }),
    'PUT',
    setting
  );
}

/**
 * Extracts preference rows from a query or upsert response without materializing records.
 * @throws When the document carries no `data`, so a failed read is never mistaken for "no rows".
 */
export function readUserPreferenceSettings(content: unknown): UserPreferenceSetting[] {
  if (!content || typeof content !== 'object' || !('data' in content)) {
    throw new Error('Invalid user preference settings response');
  }
  const data = content.data;
  const resources = Array.isArray(data) ? data : [data];
  return resources.flatMap((resource) => {
    const attributes =
      resource && typeof resource === 'object' && 'attributes' in resource
        ? (resource.attributes as Record<string, unknown> | null)
        : null;
    const value = attributes?.['value'];
    if (
      typeof attributes?.['subject'] !== 'string' ||
      typeof attributes['predicate'] !== 'string' ||
      !value ||
      typeof value !== 'object' ||
      Array.isArray(value)
    ) {
      return [];
    }
    return [
      {
        subject: attributes['subject'],
        predicate: attributes['predicate'],
        value: value as Record<string, unknown>,
      },
    ];
  });
}
